import { MAX_TRACKED_JOB_ID_LENGTH } from "../trackedJobIdLimit";
import {
  JOB_CONTINUITY_SCHEMA_VERSION,
  type ApprovalDecisionRequest,
  type ApprovalHandoffContract,
  type ApprovalHandoffOptions,
  type ApprovalHandoffStatus,
  type ApprovalPreviewSource,
  type ArtifactDeepLinkContract,
  type ArtifactDeepLinkInput,
  type BuildJobContinuityOptions,
  type ContinuityTargetKind,
  type JobContinuityLifecycle,
  type JobContinuityParseErrorCode,
  type JobContinuityParseResult,
  type JobContinuitySnapshot,
  type LegacyJobContinuitySource,
  type LegacyJobStatus,
  type NotificationHandoffContract,
  type NotificationHandoffInput,
  type NotificationHandoffStatus,
  type ResumableJobContract,
} from "./contracts";

const MAX_IDENTIFIER_LENGTH = 256;
const MAX_TYPE_LENGTH = 128;
const MAX_TEXT_LENGTH = 1_000;
const MAX_ARTIFACTS = 50;
const MIN_POLL_AFTER_MS = 1_000;
const MAX_POLL_AFTER_MS = 60_000;
const DEFAULT_POLL_AFTER_MS = 3_000;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/;

const LEGACY_JOB_STATUSES = new Set<LegacyJobStatus>([
  "pending",
  "queued",
  "claimed",
  "processing",
  "completed",
  "failed",
  "cancelled",
]);
const APPROVAL_STATUSES = new Set<ApprovalHandoffStatus>([
  "pending",
  "approved",
  "rejected",
  "expired",
  "blocked",
]);
const NOTIFICATION_STATUSES = new Set<NotificationHandoffStatus>([
  "pending",
  "delivered",
  "opened",
  "dismissed",
  "failed",
  "suppressed",
]);
const TARGET_KINDS = new Set<ContinuityTargetKind>(["job", "approval", "artifact"]);

class ContinuityContractError extends Error {
  constructor(
    readonly code: JobContinuityParseErrorCode,
    message: string,
    readonly path?: string,
  ) {
    super(message);
    this.name = "ContinuityContractError";
  }
}

function asRecord(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new ContinuityContractError("invalid_payload", `${path} must be an object.`, path);
  }
  return value as Record<string, unknown>;
}

function requiredString(
  value: unknown,
  path: string,
  maxLength = MAX_IDENTIFIER_LENGTH,
): string {
  if (typeof value !== "string") {
    throw new ContinuityContractError("invalid_identifier", `${path} must be a string.`, path);
  }
  const normalized = value.trim();
  if (
    !normalized ||
    normalized.length > maxLength ||
    CONTROL_CHARACTER_PATTERN.test(normalized)
  ) {
    throw new ContinuityContractError("invalid_identifier", `${path} is not a valid identifier.`, path);
  }
  return normalized;
}

function optionalString(value: unknown, path: string, maxLength = MAX_TEXT_LENGTH): string | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value !== "string") {
    throw new ContinuityContractError("invalid_payload", `${path} must be a string.`, path);
  }
  const normalized = value.trim();
  if (!normalized || normalized.length > maxLength || CONTROL_CHARACTER_PATTERN.test(normalized)) {
    throw new ContinuityContractError("invalid_payload", `${path} is invalid.`, path);
  }
  return normalized;
}

function timestamp(value: unknown, path: string, fallback?: number): number {
  const candidate = value === undefined ? fallback : value;
  if (typeof candidate !== "number" || !Number.isFinite(candidate) || candidate < 0) {
    throw new ContinuityContractError("invalid_timestamp", `${path} must be a non-negative timestamp.`, path);
  }
  return Math.trunc(candidate);
}

function nonNegativeInteger(value: unknown, path: string, fallback = 0): number {
  const candidate = value === undefined ? fallback : value;
  if (typeof candidate !== "number" || !Number.isSafeInteger(candidate) || candidate < 0) {
    throw new ContinuityContractError("invalid_payload", `${path} must be a non-negative integer.`, path);
  }
  return candidate;
}

function normalizeLegacyStatus(value: unknown, path: string): LegacyJobStatus {
  if (typeof value !== "string" || !LEGACY_JOB_STATUSES.has(value as LegacyJobStatus)) {
    throw new ContinuityContractError("invalid_status", `${path} is not a supported job status.`, path);
  }
  return value as LegacyJobStatus;
}

function lifecycleForStatus(status: LegacyJobStatus): JobContinuityLifecycle {
  switch (status) {
    case "pending":
    case "queued":
      return "queued";
    case "claimed":
    case "processing":
      return "running";
    case "completed":
      return "completed";
    case "failed":
      return "failed";
    case "cancelled":
      return "cancelled";
  }
}

function isTerminalLifecycle(lifecycle: JobContinuityLifecycle): boolean {
  return lifecycle === "completed" || lifecycle === "failed" || lifecycle === "cancelled";
}

function boundedPollAfterMs(value: unknown): number {
  if (value === undefined) return DEFAULT_POLL_AFTER_MS;
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new ContinuityContractError("invalid_payload", "pollAfterMs must be a number.", "job.resume.pollAfterMs");
  }
  return Math.min(MAX_POLL_AFTER_MS, Math.max(MIN_POLL_AFTER_MS, Math.trunc(value)));
}

function normalizeSameOriginPath(value: unknown, path: string, apiOnly = false): string {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) {
    throw new ContinuityContractError("invalid_path", `${path} must be a same-origin path.`, path);
  }
  if (value.includes("\\") || CONTROL_CHARACTER_PATTERN.test(value)) {
    throw new ContinuityContractError("invalid_path", `${path} contains unsafe characters.`, path);
  }

  let parsed: URL;
  try {
    parsed = new URL(value, "https://continuity.invalid");
  } catch {
    throw new ContinuityContractError("invalid_path", `${path} is not a valid URL path.`, path);
  }
  if (parsed.origin !== "https://continuity.invalid" || parsed.hash) {
    throw new ContinuityContractError("invalid_path", `${path} must not contain an origin or fragment.`, path);
  }
  if (apiOnly && !parsed.pathname.startsWith("/api/")) {
    throw new ContinuityContractError("invalid_path", `${path} must target an API route.`, path);
  }
  return `${parsed.pathname}${parsed.search}`;
}

function stringArray(value: unknown, path: string): string[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) {
    throw new ContinuityContractError("invalid_payload", `${path} must be an array.`, path);
  }
  return Array.from(new Set(value.map((entry, index) => requiredString(entry, `${path}[${index}]`))));
}

export function buildJobStatusPath(jobId: string): string {
  const normalizedJobId = requiredString(jobId, "jobId", MAX_TRACKED_JOB_ID_LENGTH);
  return `/api/getJobStatus?${new URLSearchParams({ jobId: normalizedJobId }).toString()}`;
}

export function buildArtifactApiPath(artifactId: string): string {
  const normalizedArtifactId = requiredString(artifactId, "artifactId");
  return `/api/artifacts?${new URLSearchParams({ id: normalizedArtifactId }).toString()}`;
}

export function buildArtifactWebPath(
  artifactId: string,
  jobId?: string,
  kind?: string,
): string {
  const params = new URLSearchParams({
    continuity: "artifact",
    artifactId: requiredString(artifactId, "artifactId"),
  });
  const normalizedKind = optionalString(kind, "artifact.kind", MAX_TYPE_LENGTH);
  if (normalizedKind) params.set("artifactKind", normalizedKind);
  if (jobId) params.set("jobId", requiredString(jobId, "jobId", MAX_TRACKED_JOB_ID_LENGTH));
  return `/?${params.toString()}`;
}

export function buildArtifactDeepLink(
  input: ArtifactDeepLinkInput,
  jobId?: string,
): ArtifactDeepLinkContract {
  const artifactId = requiredString(input.artifactId, "artifact.artifactId");
  const normalizedJobId = jobId ? requiredString(jobId, "artifact.jobId", MAX_TRACKED_JOB_ID_LENGTH) : undefined;
  const kind = optionalString(input.kind, "artifact.kind", MAX_TYPE_LENGTH);
  const title = optionalString(input.title, "artifact.title");
  return {
    artifactId,
    ...(normalizedJobId ? { jobId: normalizedJobId } : {}),
    ...(kind ? { kind } : {}),
    ...(title ? { title } : {}),
    auth: "session",
    apiPath: buildArtifactApiPath(artifactId),
    webPath: buildArtifactWebPath(artifactId, normalizedJobId, kind),
  };
}

function buildDecisionRequest(
  path: string,
  approvalId: string,
  decision: "approve" | "reject",
  idempotencyKey: string,
): ApprovalDecisionRequest {
  return {
    method: "POST",
    auth: "session",
    path,
    contentType: "application/json",
    body: { approvalId, decision, idempotencyKey },
  };
}

export function buildApprovalHandoff(
  preview: ApprovalPreviewSource,
  options: ApprovalHandoffOptions = {},
): ApprovalHandoffContract | null {
  const confirmation = preview.confirmation;
  if (confirmation.requirement === "none" && confirmation.status === "not_required") return null;

  const previewId = requiredString(preview.previewId, "approval.previewId");
  const actionId = requiredString(preview.actionId, "approval.actionId");
  const approvalId = `approval:${previewId}`;
  const requestedAt = timestamp(options.requestedAt, "approval.requestedAt", options.now ?? Date.now());
  const expiresAt = options.expiresAt === undefined
    ? undefined
    : timestamp(options.expiresAt, "approval.expiresAt");
  const now = timestamp(options.now, "approval.now", requestedAt);
  const expired = expiresAt !== undefined && expiresAt <= now;
  const sourceStatus = confirmation.status;
  const status: ApprovalHandoffStatus = expired
    ? "expired"
    : sourceStatus === "confirmed"
      ? "approved"
      : sourceStatus === "rejected"
        ? "rejected"
        : sourceStatus === "blocked" || confirmation.requirement === "blocked"
          ? "blocked"
          : "pending";

  if (status === "pending" && (confirmation.allowsExecution || confirmation.requirement !== "confirm")) {
    throw new ContinuityContractError(
      "invalid_approval",
      "Pending approvals must explicitly block execution until confirmation.",
      "approval",
    );
  }

  const base: ApprovalHandoffContract = {
    approvalId,
    actionId,
    previewId,
    status,
    title: requiredString(preview.title, "approval.title", MAX_TEXT_LENGTH),
    summary: requiredString(preview.summary, "approval.summary", MAX_TEXT_LENGTH),
    reason: requiredString(confirmation.reason, "approval.reason", MAX_TEXT_LENGTH),
    requestedAt,
    ...(expiresAt !== undefined ? { expiresAt } : {}),
    canDecide: status === "pending",
  };

  if (status !== "pending") return base;
  const decisionPath = normalizeSameOriginPath(options.decisionPath, "approval.decisionPath", true);
  const idempotencyKey = requiredString(
    preview.idempotency.key,
    "approval.idempotencyKey",
    MAX_TEXT_LENGTH,
  );
  return {
    ...base,
    decisions: {
      approve: buildDecisionRequest(decisionPath, approvalId, "approve", idempotencyKey),
      reject: buildDecisionRequest(decisionPath, approvalId, "reject", idempotencyKey),
    },
  };
}

function targetWebPath(kind: ContinuityTargetKind, id: string): string {
  if (kind === "artifact") return buildArtifactWebPath(id);
  const params = new URLSearchParams({ continuity: kind, [`${kind}Id`]: id });
  return `/?${params.toString()}`;
}

export function buildNotificationHandoff(input: NotificationHandoffInput): NotificationHandoffContract {
  const status = input.status;
  if (typeof status !== "string" || !NOTIFICATION_STATUSES.has(status as NotificationHandoffStatus)) {
    throw new ContinuityContractError(
      "invalid_notification",
      "notification.status is not supported.",
      "notification.status",
    );
  }
  const kind = input.target?.kind;
  if (typeof kind !== "string" || !TARGET_KINDS.has(kind as ContinuityTargetKind)) {
    throw new ContinuityContractError(
      "invalid_notification",
      "notification.target.kind is not supported.",
      "notification.target.kind",
    );
  }
  const targetKind = kind as ContinuityTargetKind;
  const targetId = requiredString(input.target.id, "notification.target.id", targetKind === "job" ? MAX_TRACKED_JOB_ID_LENGTH : MAX_IDENTIFIER_LENGTH);
  const expectedWebPath = targetWebPath(targetKind, targetId);
  if (input.target.webPath !== undefined) {
    const suppliedWebPath = normalizeSameOriginPath(input.target.webPath, "notification.target.webPath");
    if (suppliedWebPath !== expectedWebPath) {
      throw new ContinuityContractError(
        "invalid_notification",
        "notification.target.webPath does not match its target.",
        "notification.target.webPath",
      );
    }
  }
  const failureCode = optionalString(input.failureCode, "notification.failureCode", MAX_TYPE_LENGTH);
  if (status === "failed" && !failureCode) {
    throw new ContinuityContractError(
      "invalid_notification",
      "Failed notification handoffs require a failureCode.",
      "notification.failureCode",
    );
  }
  if (status !== "failed" && failureCode) {
    throw new ContinuityContractError(
      "invalid_notification",
      "failureCode is only valid for failed notification handoffs.",
      "notification.failureCode",
    );
  }
  return {
    handoffId: requiredString(input.handoffId, "notification.handoffId"),
    notificationId: requiredString(input.notificationId, "notification.notificationId"),
    dedupeKey: requiredString(input.dedupeKey, "notification.dedupeKey", MAX_TEXT_LENGTH),
    status: status as NotificationHandoffStatus,
    revision: nonNegativeInteger(input.revision, "notification.revision"),
    updatedAt: timestamp(input.updatedAt, "notification.updatedAt", Date.now()),
    target: { kind: targetKind, id: targetId, webPath: expectedWebPath },
    ...(failureCode ? { failureCode } : {}),
  };
}

export function mergeNotificationHandoffs(
  current: NotificationHandoffContract,
  incoming: NotificationHandoffContract,
): NotificationHandoffContract {
  const currentNormalized = buildNotificationHandoff(current);
  const incomingNormalized = buildNotificationHandoff(incoming);
  const sameIdentity =
    currentNormalized.handoffId === incomingNormalized.handoffId &&
    currentNormalized.notificationId === incomingNormalized.notificationId &&
    currentNormalized.dedupeKey === incomingNormalized.dedupeKey &&
    currentNormalized.target.kind === incomingNormalized.target.kind &&
    currentNormalized.target.id === incomingNormalized.target.id;
  if (!sameIdentity) {
    throw new ContinuityContractError(
      "inconsistent_state",
      "Notification handoffs with different identities cannot be merged.",
      "notification",
    );
  }
  if (incomingNormalized.revision < currentNormalized.revision) return currentNormalized;
  if (incomingNormalized.revision === currentNormalized.revision) {
    const sameRevisionValue =
      incomingNormalized.status === currentNormalized.status &&
      incomingNormalized.failureCode === currentNormalized.failureCode;
    if (!sameRevisionValue) {
      throw new ContinuityContractError(
        "inconsistent_state",
        "A notification revision cannot represent conflicting states.",
        "notification.revision",
      );
    }
    return incomingNormalized.updatedAt > currentNormalized.updatedAt
      ? incomingNormalized
      : currentNormalized;
  }
  if (incomingNormalized.updatedAt < currentNormalized.updatedAt) {
    throw new ContinuityContractError(
      "inconsistent_state",
      "A newer notification revision cannot have an older timestamp.",
      "notification.updatedAt",
    );
  }
  return incomingNormalized;
}

function progressFromLegacy(source: LegacyJobContinuitySource): ResumableJobContract["progress"] {
  const message = optionalString(source.progress, "job.progress.message", MAX_TEXT_LENGTH);
  const rawPercent = source.progressPercent ?? source.percentage;
  let percent: number | undefined;
  if (rawPercent !== undefined && rawPercent !== null) {
    const numeric = Number(rawPercent);
    if (!Number.isFinite(numeric)) {
      throw new ContinuityContractError("invalid_payload", "Job progress percent must be numeric.", "job.progress.percent");
    }
    percent = Math.min(100, Math.max(0, numeric));
  }
  return message !== undefined || percent !== undefined ? { ...(message ? { message } : {}), ...(percent !== undefined ? { percent } : {}) } : undefined;
}

export function buildJobContinuitySnapshot(
  source: LegacyJobContinuitySource,
  options: BuildJobContinuityOptions = {},
): JobContinuitySnapshot {
  const jobId = requiredString(source.jobId, "job.jobId", MAX_TRACKED_JOB_ID_LENGTH);
  const legacyStatus = normalizeLegacyStatus(source.status, "job.legacyStatus");
  const generatedAt = timestamp(options.generatedAt, "generatedAt", Date.now());
  const createdAt = timestamp(source.createdAt, "job.createdAt", generatedAt);
  const updatedAt = timestamp(source.updatedAt, "job.updatedAt", createdAt);
  if (updatedAt < createdAt) {
    throw new ContinuityContractError(
      "inconsistent_state",
      "job.updatedAt cannot be older than job.createdAt.",
      "job.updatedAt",
    );
  }
  const approval = options.approval ?? undefined;
  let lifecycle = lifecycleForStatus(legacyStatus);
  if (approval?.status === "pending") {
    if (isTerminalLifecycle(lifecycle)) {
      throw new ContinuityContractError(
        "inconsistent_state",
        "A terminal job cannot have a pending approval.",
        "approval.status",
      );
    }
    lifecycle = "waiting_for_approval";
  }
  const terminal = isTerminalLifecycle(lifecycle);
  const artifacts = (options.artifacts || []).map((artifact) => buildArtifactDeepLink(artifact, jobId));
  if (artifacts.length > MAX_ARTIFACTS) {
    throw new ContinuityContractError(
      "invalid_artifact",
      `A continuity snapshot may contain at most ${MAX_ARTIFACTS} artifacts.`,
      "artifacts",
    );
  }
  const dedupedArtifacts = Array.from(new Map(artifacts.map((artifact) => [artifact.artifactId, artifact])).values());
  const parentJobIdSource = source.parentJobId ?? source.orchestrationJobId;
  const parentJobId = parentJobIdSource === undefined
    ? undefined
    : requiredString(parentJobIdSource, "job.parentJobId", MAX_TRACKED_JOB_ID_LENGTH);
  const progress = progressFromLegacy(source);
  const job: ResumableJobContract = {
    jobId,
    type: optionalString(source.type, "job.type", MAX_TYPE_LENGTH) || "unknown",
    legacyStatus,
    lifecycle,
    terminal,
    createdAt,
    updatedAt,
    ...(progress ? { progress } : {}),
    ...(parentJobId ? { parentJobId } : {}),
    runIds: stringArray(source.runIds, "job.runIds"),
    resume: {
      mode: "poll",
      auth: "session",
      statusPath: buildJobStatusPath(jobId),
      pollAfterMs: boundedPollAfterMs(options.pollAfterMs),
      checkpoint: updatedAt,
      canResume: !terminal,
    },
  };
  return {
    schemaVersion: JOB_CONTINUITY_SCHEMA_VERSION,
    generatedAt,
    job,
    ...(approval ? { approval } : {}),
    artifacts: dedupedArtifacts,
    ...(options.notification ? { notification: buildNotificationHandoff(options.notification) } : {}),
  };
}

function parseDecisionRequest(
  value: unknown,
  path: string,
  approvalId: string,
  decision: "approve" | "reject",
): ApprovalDecisionRequest {
  const record = asRecord(value, path);
  if (record.method !== "POST" || record.auth !== "session" || record.contentType !== "application/json") {
    throw new ContinuityContractError("invalid_approval", `${path} has an unsupported request contract.`, path);
  }
  const body = asRecord(record.body, `${path}.body`);
  if (body.approvalId !== approvalId || body.decision !== decision) {
    throw new ContinuityContractError("invalid_approval", `${path}.body does not match the approval.`, `${path}.body`);
  }
  return buildDecisionRequest(
    normalizeSameOriginPath(record.path, `${path}.path`, true),
    approvalId,
    decision,
    requiredString(body.idempotencyKey, `${path}.body.idempotencyKey`, MAX_TEXT_LENGTH),
  );
}

function parseApproval(value: unknown): ApprovalHandoffContract {
  const record = asRecord(value, "approval");
  const status = record.status;
  if (typeof status !== "string" || !APPROVAL_STATUSES.has(status as ApprovalHandoffStatus)) {
    throw new ContinuityContractError("invalid_approval", "approval.status is not supported.", "approval.status");
  }
  const approvalId = requiredString(record.approvalId, "approval.approvalId");
  const previewId = requiredString(record.previewId, "approval.previewId");
  if (approvalId !== `approval:${previewId}`) {
    throw new ContinuityContractError(
      "invalid_approval",
      "approval.approvalId does not match its preview.",
      "approval.approvalId",
    );
  }
  const canDecide = record.canDecide === true;
  const base: ApprovalHandoffContract = {
    approvalId,
    actionId: requiredString(record.actionId, "approval.actionId"),
    previewId,
    status: status as ApprovalHandoffStatus,
    title: requiredString(record.title, "approval.title", MAX_TEXT_LENGTH),
    summary: requiredString(record.summary, "approval.summary", MAX_TEXT_LENGTH),
    reason: requiredString(record.reason, "approval.reason", MAX_TEXT_LENGTH),
    requestedAt: timestamp(record.requestedAt, "approval.requestedAt"),
    ...(record.expiresAt === undefined ? {} : { expiresAt: timestamp(record.expiresAt, "approval.expiresAt") }),
    canDecide,
  };
  if (status !== "pending") {
    if (canDecide || record.decisions !== undefined) {
      throw new ContinuityContractError(
        "invalid_approval",
        "Only pending approvals may include decision requests.",
        "approval",
      );
    }
    return base;
  }
  if (!canDecide) {
    throw new ContinuityContractError("invalid_approval", "Pending approvals must be decidable.", "approval.canDecide");
  }
  const decisions = asRecord(record.decisions, "approval.decisions");
  const approve = parseDecisionRequest(decisions.approve, "approval.decisions.approve", approvalId, "approve");
  const reject = parseDecisionRequest(decisions.reject, "approval.decisions.reject", approvalId, "reject");
  if (
    approve.path !== reject.path ||
    approve.body.idempotencyKey !== reject.body.idempotencyKey
  ) {
    throw new ContinuityContractError(
      "invalid_approval",
      "Approval decisions must share one endpoint and idempotency key.",
      "approval.decisions",
    );
  }
  return {
    ...base,
    decisions: {
      approve,
      reject,
    },
  };
}

function parseArtifact(value: unknown, jobId: string, index: number): ArtifactDeepLinkContract {
  const path = `artifacts[${index}]`;
  const record = asRecord(value, path);
  const artifact = buildArtifactDeepLink({
    artifactId: record.artifactId,
    kind: record.kind,
    title: record.title,
  }, record.jobId === undefined ? undefined : requiredString(record.jobId, `${path}.jobId`, MAX_TRACKED_JOB_ID_LENGTH));
  if (artifact.jobId && artifact.jobId !== jobId) {
    throw new ContinuityContractError("invalid_artifact", `${path}.jobId does not match the snapshot job.`, `${path}.jobId`);
  }
  if (record.auth !== "session" || record.apiPath !== artifact.apiPath || record.webPath !== artifact.webPath) {
    throw new ContinuityContractError("invalid_artifact", `${path} contains an unsafe or mismatched link.`, path);
  }
  return artifact;
}

function parseVersionedSnapshot(value: unknown): JobContinuitySnapshot {
  const record = asRecord(value, "continuity");
  if (record.schemaVersion !== JOB_CONTINUITY_SCHEMA_VERSION) {
    throw new ContinuityContractError(
      "unsupported_version",
      `Unsupported job continuity schema version: ${String(record.schemaVersion || "missing")}.`,
      "schemaVersion",
    );
  }
  const jobRecord = asRecord(record.job, "job");
  const jobId = requiredString(jobRecord.jobId, "job.jobId", MAX_TRACKED_JOB_ID_LENGTH);
  const legacyStatus = normalizeLegacyStatus(jobRecord.legacyStatus, "job.legacyStatus");
  const approval = record.approval === undefined ? undefined : parseApproval(record.approval);
  let expectedLifecycle = lifecycleForStatus(legacyStatus);
  if (approval?.status === "pending") {
    if (isTerminalLifecycle(expectedLifecycle)) {
      throw new ContinuityContractError(
        "inconsistent_state",
        "A terminal job cannot have a pending approval.",
        "approval.status",
      );
    }
    expectedLifecycle = "waiting_for_approval";
  }
  if (jobRecord.lifecycle !== expectedLifecycle) {
    throw new ContinuityContractError(
      "inconsistent_state",
      "job.lifecycle does not match its status and approval state.",
      "job.lifecycle",
    );
  }
  const terminal = isTerminalLifecycle(expectedLifecycle);
  if (jobRecord.terminal !== terminal) {
    throw new ContinuityContractError("inconsistent_state", "job.terminal does not match lifecycle.", "job.terminal");
  }
  const createdAt = timestamp(jobRecord.createdAt, "job.createdAt");
  const updatedAt = timestamp(jobRecord.updatedAt, "job.updatedAt");
  if (updatedAt < createdAt) {
    throw new ContinuityContractError("inconsistent_state", "job.updatedAt cannot precede job.createdAt.", "job.updatedAt");
  }
  const resumeRecord = asRecord(jobRecord.resume, "job.resume");
  const expectedStatusPath = buildJobStatusPath(jobId);
  const pollAfterMs = boundedPollAfterMs(resumeRecord.pollAfterMs);
  if (
    resumeRecord.mode !== "poll" ||
    resumeRecord.auth !== "session" ||
    resumeRecord.statusPath !== expectedStatusPath ||
    resumeRecord.checkpoint !== updatedAt ||
    resumeRecord.canResume !== !terminal ||
    resumeRecord.pollAfterMs !== pollAfterMs
  ) {
    throw new ContinuityContractError("inconsistent_state", "job.resume does not match the job state.", "job.resume");
  }
  const progressRecord = jobRecord.progress === undefined ? undefined : asRecord(jobRecord.progress, "job.progress");
  const progressMessage = progressRecord ? optionalString(progressRecord.message, "job.progress.message") : undefined;
  const progressPercent = progressRecord?.percent === undefined ? undefined : Number(progressRecord.percent);
  if (progressPercent !== undefined && (!Number.isFinite(progressPercent) || progressPercent < 0 || progressPercent > 100)) {
    throw new ContinuityContractError("invalid_payload", "job.progress.percent must be between 0 and 100.", "job.progress.percent");
  }
  const rawArtifacts = record.artifacts;
  if (!Array.isArray(rawArtifacts) || rawArtifacts.length > MAX_ARTIFACTS) {
    throw new ContinuityContractError("invalid_artifact", "artifacts must be a bounded array.", "artifacts");
  }
  const artifacts = rawArtifacts.map((artifact, index) => parseArtifact(artifact, jobId, index));
  const artifactIds = new Set(artifacts.map((artifact) => artifact.artifactId));
  if (artifactIds.size !== artifacts.length) {
    throw new ContinuityContractError("invalid_artifact", "artifacts must not contain duplicate ids.", "artifacts");
  }
  const notification = record.notification === undefined
    ? undefined
    : buildNotificationHandoff(record.notification as unknown as NotificationHandoffInput);
  const job: ResumableJobContract = {
    jobId,
    type: requiredString(jobRecord.type, "job.type", MAX_TYPE_LENGTH),
    legacyStatus,
    lifecycle: expectedLifecycle,
    terminal,
    createdAt,
    updatedAt,
    ...(progressRecord ? { progress: { ...(progressMessage ? { message: progressMessage } : {}), ...(progressPercent !== undefined ? { percent: progressPercent } : {}) } } : {}),
    ...(jobRecord.parentJobId === undefined ? {} : { parentJobId: requiredString(jobRecord.parentJobId, "job.parentJobId", MAX_TRACKED_JOB_ID_LENGTH) }),
    runIds: stringArray(jobRecord.runIds, "job.runIds"),
    resume: {
      mode: "poll",
      auth: "session",
      statusPath: expectedStatusPath,
      pollAfterMs,
      checkpoint: updatedAt,
      canResume: !terminal,
    },
  };
  return {
    schemaVersion: JOB_CONTINUITY_SCHEMA_VERSION,
    generatedAt: timestamp(record.generatedAt, "generatedAt"),
    job,
    ...(approval ? { approval } : {}),
    artifacts,
    ...(notification ? { notification } : {}),
  };
}

function toParseFailure(error: unknown): JobContinuityParseResult {
  if (error instanceof ContinuityContractError) {
    return {
      ok: false,
      error: { code: error.code, message: error.message, ...(error.path ? { path: error.path } : {}) },
    };
  }
  return {
    ok: false,
    error: { code: "invalid_payload", message: error instanceof Error ? error.message : "Invalid continuity payload." },
  };
}

export function parseJobContinuitySnapshot(value: unknown): JobContinuityParseResult {
  try {
    return { ok: true, source: "versioned", value: parseVersionedSnapshot(value) };
  } catch (error) {
    return toParseFailure(error);
  }
}

export function normalizeJobContinuityResponse(value: unknown): JobContinuityParseResult {
  try {
    const record = asRecord(value, "response");
    if (Object.prototype.hasOwnProperty.call(record, "continuity")) {
      return { ok: true, source: "versioned", value: parseVersionedSnapshot(record.continuity) };
    }
    if (Object.prototype.hasOwnProperty.call(record, "schemaVersion")) {
      return { ok: true, source: "versioned", value: parseVersionedSnapshot(record) };
    }
    return {
      ok: true,
      source: "legacy",
      value: buildJobContinuitySnapshot(record as LegacyJobContinuitySource),
    };
  } catch (error) {
    return toParseFailure(error);
  }
}
